import { Pen, Plus, Trash2, X } from "lucide-react";
import { FC, useMemo, useState } from "react";
import {
  FormattedMessage,
  MessageDescriptor,
  defineMessages,
  useIntl,
} from "react-intl";
import { User, UserStatus } from "~/api/model";
import {
  useGetCurrentUser,
  useGetUserGuestAccesses,
  useListUsers,
} from "~/api/users/users";
import { Loader } from "~/components/Loader";
import { Badge } from "~/components/ui/badge";
import { Button } from "~/components/ui/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "~/components/ui/table";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "~/components/ui/tabs";
import { UserAvatar } from "~/components/ui/user-avatar";
import { useOrganizationParams } from "~/helpers";
import { useTrackPageVisit } from "~/hooks/useTrackPageVisit";
import { shortRelativeTime } from "~/utils/date";
import { ChangeRoleDialog } from "./team/ChangeRoleDialog";
import { DeleteUserDialog } from "./team/DeleteUserDialog";
import { GuestSecureLinksDialog } from "./team/GuestSecureLinksDialog";
import { InviteUserDialog } from "./team/InviteUserDialog";
import { SettingsUserRole } from "./types";

interface TableHeader {
  id: string;
  defaultMessage: string;
  className?: string;
}

const roles: Record<SettingsUserRole, MessageDescriptor> = defineMessages({
  OWNER: {
    id: "settings.team.role.owner",
    defaultMessage: "Admin",
  },
  MEMBER: {
    id: "settings.team.role.member",
    defaultMessage: "Member",
  },
  GUEST: {
    id: "settings.team.role.guest",
    defaultMessage: "Guest",
  },
});

interface GuestRowProps {
  user: User;
  organizationId: string;
  locale: string;
  onSecureLinksClick: (user: User) => void;
  onDeleteClick: (user: User) => void;
}

const GuestRow: FC<GuestRowProps> = ({
  user,
  organizationId,
  locale,
  onSecureLinksClick,
  onDeleteClick,
}) => {
  const { data: guestAccesses } = useGetUserGuestAccesses(
    organizationId,
    user.id
  );

  const hasActiveLinks = guestAccesses && guestAccesses.length > 0;
  const activeLinksCount = guestAccesses?.length ?? 0;
  const hasBeenSeen = user.updatedAt !== user.createdAt;
  const activityTimeAgo = shortRelativeTime(
    hasBeenSeen ? user.updatedAt : user.createdAt,
    locale
  );

  return (
    <TableRow>
      <TableCell className="text-muted-foreground p-4 whitespace-nowrap overflow-hidden text-ellipsis">
        {user.email}
      </TableCell>
      <TableCell className="text-muted-foreground p-4 whitespace-nowrap">
        {activeLinksCount > 0 && activeLinksCount}
      </TableCell>
      <TableCell className="text-muted-foreground p-4 whitespace-nowrap">
        {hasBeenSeen ? (
          <FormattedMessage
            id="settings.team.guests.seenAgo"
            defaultMessage="Seen {timeAgo}"
            values={{ timeAgo: activityTimeAgo }}
          />
        ) : (
          <FormattedMessage
            id="settings.team.guests.invitedAgo"
            defaultMessage="Invited {timeAgo}"
            values={{ timeAgo: activityTimeAgo }}
          />
        )}
      </TableCell>
      <TableCell>
        <div className="flex justify-end">
          {hasActiveLinks && (
            <Button
              variant="ghost"
              size="icon"
              onClick={() => {
                onSecureLinksClick(user);
              }}
            >
              <Pen className="h-4 w-4" />
            </Button>
          )}
          <Button
            variant="ghost"
            size="icon"
            onClick={() => {
              onDeleteClick(user);
            }}
          >
            <Trash2 className="h-4 w-4" />
          </Button>
        </div>
      </TableCell>
    </TableRow>
  );
};

const sortUsers = (a: User, b: User): number => {
  const aDeactivated =
    (a as User & { deactivatedAt?: string | null }).deactivatedAt != null;
  const bDeactivated =
    (b as User & { deactivatedAt?: string | null }).deactivatedAt != null;

  if (aDeactivated && !bDeactivated) {
    return 1;
  }
  if (!aDeactivated && bDeactivated) {
    return -1;
  }
  if (aDeactivated && bDeactivated) {
    return a.name.localeCompare(b.name);
  }

  if (
    a.status === UserStatus.FORCE_CHANGE_PASSWORD &&
    b.status !== UserStatus.FORCE_CHANGE_PASSWORD
  ) {
    return -1;
  }
  if (
    a.status !== UserStatus.FORCE_CHANGE_PASSWORD &&
    b.status === UserStatus.FORCE_CHANGE_PASSWORD
  ) {
    return 1;
  }
  return a.name.localeCompare(b.name);
};

export const SettingsTeam: FC = () => {
  useTrackPageVisit("app/settings_team/visit");
  const [showChangeRoleDialog, setShowChangeRoleDialog] = useState(false);
  const [showInviteUserDialog, setShowInviteUserDialog] = useState(false);
  const [showDeleteUserDialog, setShowDeleteUserDialog] = useState(false);
  const [showGuestSecureLinksDialog, setShowGuestSecureLinksDialog] =
    useState(false);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [activeTab, setActiveTab] = useState<"members" | "guests">("members");
  const { organizationId } = useOrganizationParams();
  const { data: users, isLoading } = useListUsers(organizationId);
  const { data: currentUser } = useGetCurrentUser();
  const intl = useIntl();
  const { formatMessage } = intl;

  const sortedUsers = useMemo(() => {
    if (!users) return [];
    return [...users].filter((user) => user.role !== "GUEST").sort(sortUsers);
  }, [users, organizationId]);

  const guestUsers = useMemo(() => {
    if (!users) return [];
    return [...users].filter((user) => user.role === "GUEST").sort(sortUsers);
  }, [users, organizationId]);

  const tableHeaders: TableHeader[] = [
    {
      id: "settings.team.table.name",
      defaultMessage: "Name",
      className: "px-4",
    },
    {
      id: "settings.team.table.email",
      defaultMessage: "Email",
      className: "px-4 w-64 whitespace-nowrap hidden sm:table-cell",
    },
    {
      id: "settings.team.table.role",
      defaultMessage: "Role",
      className: "px-4 w-20 sm:w-32 whitespace-nowrap",
    },
    {
      id: "",
      defaultMessage: "",
      className: "w-16 sm:w-24 whitespace-nowrap",
    },
  ];

  const handleEditClick = (user: User) => {
    setSelectedUser(user);
    setShowChangeRoleDialog(true);
  };

  const handleInviteClick = () => {
    setShowInviteUserDialog(true);
  };

  const handleDeleteClick = (user: User) => {
    setSelectedUser(user);
    setShowDeleteUserDialog(true);
  };

  const handleGuestSecureLinksClick = (user: User) => {
    setSelectedUser(user);
    setShowGuestSecureLinksDialog(true);
  };

  if (isLoading)
    return (
      <div className="flex justify-center items-center w-full max-w-[924px] h-[50vh]">
        <Loader />
      </div>
    );

  return (
    <div className="flex flex-col gap-6 mt-2 w-full">
      <div className="flex flex-col sm:flex-row justify-between gap-4">
        <div className="flex flex-col gap-2">
          <h3 className="text-2xl font-semibold">
            <FormattedMessage id="settings.team" defaultMessage={"Team"} />
          </h3>
          <span className="text-sm text-muted-foreground">
            <FormattedMessage
              id="settings.team.hint"
              defaultMessage={"Managing the members of your organization"}
            />
          </span>
        </div>
        {activeTab === "members" && (
          <Button className="self-start" onClick={handleInviteClick}>
            <Plus className="w-4 h-4 mr-2" />
            <FormattedMessage
              id="settings.team.invite"
              defaultMessage="Invite"
            />
          </Button>
        )}
      </div>
      <Tabs
        value={activeTab}
        onValueChange={(value) => setActiveTab(value as "members" | "guests")}
      >
        <TabsList>
          <TabsTrigger value="members">
            <FormattedMessage
              id="settings.team.tabs.members"
              defaultMessage="Members"
            />
          </TabsTrigger>
          <TabsTrigger value="guests">
            <FormattedMessage
              id="settings.team.tabs.guests"
              defaultMessage="Guests"
            />
          </TabsTrigger>
        </TabsList>
        <TabsContent value="members" className="mt-4">
          <Table className="w-full">
            <TableHeader>
              <TableRow className="!text-zinc-500 !text-sm !font-medium">
                {tableHeaders.map((header) => (
                  <TableHead key={header.id} className={header.className}>
                    {header.id && (
                      <FormattedMessage
                        id={header.id}
                        defaultMessage={header.defaultMessage}
                      />
                    )}
                  </TableHead>
                ))}
              </TableRow>
            </TableHeader>
            <TableBody>
              {sortedUsers.map((user) => {
                const isDeactivated =
                  (user as User & { deactivatedAt?: string | null })
                    .deactivatedAt != null;
                return (
                  <TableRow
                    key={user.id}
                    className={isDeactivated ? "opacity-50" : ""}
                  >
                    <TableCell className="font-semibold p-4">
                      {user.status === UserStatus.FORCE_CHANGE_PASSWORD ? (
                        <div className="flex items-center gap-2">
                          <div className="w-6 h-6 bg-[#E4E4E7] rounded-full flex-shrink-0" />
                          <div className="flex flex-col min-w-0">
                            <span className="text-sm font-medium text-muted-foreground">
                              <FormattedMessage
                                id="settings.team.invitationSent"
                                defaultMessage="Invitation sent"
                              />
                            </span>
                            <span className="text-xs text-muted-foreground truncate sm:hidden">
                              {user.email}
                            </span>
                          </div>
                        </div>
                      ) : (
                        <div className="flex items-center gap-2">
                          <UserAvatar name={user.name} size="sm" />
                          <div className="flex flex-col min-w-0">
                            <div className="flex items-center gap-2">
                              <span className="text-sm font-medium truncate">
                                {user.name}
                              </span>
                              {isDeactivated && (
                                <Badge
                                  variant="outline"
                                  className="text-xs flex-shrink-0"
                                >
                                  <FormattedMessage
                                    id="settings.team.deactivated"
                                    defaultMessage="Deactivated"
                                  />
                                </Badge>
                              )}
                            </div>
                            <span className="text-xs text-muted-foreground truncate sm:hidden">
                              {user.email}
                            </span>
                          </div>
                        </div>
                      )}
                    </TableCell>
                    <TableCell className="text-muted-foreground p-4 whitespace-nowrap overflow-hidden text-ellipsis hidden sm:table-cell">
                      {user.email}
                    </TableCell>
                    <TableCell className="text-muted-foreground p-4 whitespace-nowrap">
                      {formatMessage(roles[user.role as SettingsUserRole])}
                    </TableCell>
                    <TableCell>
                      {user.id !== currentUser?.id &&
                        user.status !== UserStatus.FORCE_CHANGE_PASSWORD && (
                          <div className="flex justify-end">
                            {!user.deactivatedAt && (
                              <Button
                                variant="ghost"
                                size="icon"
                                onClick={() => {
                                  handleEditClick(user);
                                }}
                              >
                                <Pen className="h-4 w-4" />
                              </Button>
                            )}
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => {
                                handleDeleteClick(user);
                              }}
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        )}
                      {user.status === UserStatus.FORCE_CHANGE_PASSWORD && (
                        <div className="flex justify-end">
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => {
                              handleDeleteClick(user);
                            }}
                          >
                            <X className="h-4 w-4" />
                          </Button>
                        </div>
                      )}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TabsContent>
        <TabsContent value="guests" className="mt-4">
          <Table className="w-full">
            <TableHeader>
              <TableRow className="!text-zinc-500 !text-sm !font-medium">
                <TableHead className="px-4">
                  <FormattedMessage
                    id="settings.team.table.email"
                    defaultMessage="Email"
                  />
                </TableHead>
                <TableHead className="px-4 w-32 whitespace-nowrap">
                  <FormattedMessage
                    id="settings.team.guests.table.links"
                    defaultMessage="Links"
                  />
                </TableHead>
                <TableHead className="px-4 w-40 whitespace-nowrap">
                  <FormattedMessage
                    id="settings.team.guests.table.activity"
                    defaultMessage="Activity"
                  />
                </TableHead>
                <TableHead className="w-24 whitespace-nowrap" />
              </TableRow>
            </TableHeader>
            <TableBody>
              {guestUsers.length === 0 ? (
                <TableRow>
                  <TableCell
                    colSpan={4}
                    className="text-center text-muted-foreground py-8"
                  >
                    <FormattedMessage
                      id="settings.team.guests.empty"
                      defaultMessage="No guests have been invited yet"
                    />
                  </TableCell>
                </TableRow>
              ) : (
                guestUsers.map((user) => (
                  <GuestRow
                    key={user.id}
                    user={user}
                    organizationId={organizationId}
                    locale={intl.locale}
                    onSecureLinksClick={handleGuestSecureLinksClick}
                    onDeleteClick={handleDeleteClick}
                  />
                ))
              )}
            </TableBody>
          </Table>
        </TabsContent>
      </Tabs>
      {showChangeRoleDialog && selectedUser && (
        <ChangeRoleDialog
          open={showChangeRoleDialog}
          setOpen={setShowChangeRoleDialog}
          user={selectedUser}
        />
      )}
      {showInviteUserDialog && (
        <InviteUserDialog
          open={showInviteUserDialog}
          setOpen={setShowInviteUserDialog}
        />
      )}
      {showDeleteUserDialog && selectedUser && (
        <DeleteUserDialog
          open={showDeleteUserDialog}
          setOpen={setShowDeleteUserDialog}
          user={selectedUser}
        />
      )}
      {showGuestSecureLinksDialog && selectedUser && (
        <GuestSecureLinksDialog
          open={showGuestSecureLinksDialog}
          onOpenChange={setShowGuestSecureLinksDialog}
          organizationId={organizationId}
          user={selectedUser}
        />
      )}
    </div>
  );
};
